// 图片打码处理模块 - 基于img_sec的工作实现
// 提供OCR引擎初始化和敏感信息打码功能

let ocr, ort;

export const initializeOCREngine = async (progressCallback) => {
    try {
        console.log('🚀 === 开始初始化OCR引擎（基于img_sec实现）===');
        
        progressCallback?.(10);
        
        // 等待ONNX Runtime加载（如果需要）
        if (typeof window.ort === 'undefined') {
            console.log('等待ONNX Runtime加载...');
            // 最多等待10秒
            for (let i = 0; i < 100; i++) {
                if (typeof window.ort !== 'undefined') {
                    break;
                }
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }
        
        if (typeof window.ort === 'undefined') {
            throw new Error('ONNX Runtime未能正确加载');
        }
        
        // 使用全局的ort变量
        ort = window.ort;
        console.log('✅ ONNX Runtime加载成功');
        
        progressCallback?.(20);
        
        // 动态导入eSearch-OCR
        const ocrModule = await import('esearch-ocr');
        ocr = ocrModule; // 直接使用导入的模块，不需要.default
        console.log('✅ eSearch-OCR导入成功');
        
        progressCallback?.(30);
        
        // 配置ONNX Runtime - 服务器环境兼容配置
        if (ort.env && ort.env.wasm) {
            // 使用绝对URL路径，避免服务器环境路径问题
            ort.env.wasm.wasmPaths = window.location.origin + '/wasm/';
            ort.env.wasm.numThreads = 1;
            ort.env.wasm.simd = true;
            
            // 禁用可能导致问题的后端
            if (ort.env.webgpu) ort.env.webgpu.disabled = true;
            if (ort.env.webgl) ort.env.webgl.disabled = true;
            
            console.log('🔧 OCR初始化前ONNX Runtime配置:', {
                wasmPaths: ort.env.wasm.wasmPaths,
                numThreads: ort.env.wasm.numThreads,
                simd: ort.env.wasm.simd,
                origin: window.location.origin
            });
        }
        
        // 验证关键对象是否存在
        if (!ort.InferenceSession) {
            throw new Error('ONNX Runtime InferenceSession未正确加载');
        }
        
        progressCallback?.(50);
        
        // 读取字典文件内容
        const dictResponse = await fetch('/ocr/ppocr_keys_v1.txt');
        const dictContent = await dictResponse.text();
        
        progressCallback?.(70);
        
        // 创建自定义的ONNX Runtime会话选项
        const sessionOptions = {
            executionProviders: [
                {
                    name: 'wasm',
                    deviceType: 'cpu',
                    wasmPaths: window.location.origin + '/wasm/',
                }
            ],
            enableCpuMemArena: true,
            enableMemPattern: true,
            logSeverityLevel: 3 // 只显示错误
        };

        console.log('🔧 使用自定义会话选项:', sessionOptions);

        // 初始化OCR引擎 - 简化配置，避免复杂的WASM加载
        const ocrEngine = await ocr.init({
            det: {
                input: '/ocr/ppocr_det.onnx',
                ratio: 0.8,
                sessionOption: sessionOptions,
            },
            rec: {
                input: '/ocr/ppocr_rec.onnx',
                decodeDic: dictContent,
                optimize: {
                    space: false
                },
                sessionOption: sessionOptions,
            },
            ort,
            dev: false,
            
            // 直接传递自定义选项
            sessionOptions: sessionOptions,
            
            // 禁用所有可能导致远程加载的选项
            preferredBackend: 'wasm',
            disableWebGL: true,
            disableWebGPU: true,
            disableWebNN: true,
        });
        
        progressCallback?.(100);
        
        console.log('🎉 OCR引擎初始化成功!');
        return ocrEngine;
        
    } catch (error) {
        console.error('❌ OCR引擎初始化失败:', error);
        throw error;
    }
};

// 从File对象加载图片
export const loadImageFromFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

// 计算编辑距离（用于模糊匹配）
const calculateEditDistance = (str1, str2) => {
    const m = str1.length;
    const n = str2.length;
    const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
    
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (str1[i - 1] === str2[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1];
            } else {
                dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
            }
        }
    }
    
    return dp[m][n];
};

// 提取敏感信息
const extractSensitiveInfoFromLine = (text) => {
    if (!text) return null;
    
    const cleanText = text.trim();
    
    // 匹配各种敏感信息字段的正则表达式
    const sensitivePatterns = [
        // 姓名字段 - 支持多种格式
        // 1. 带冒号或空格分隔
        { field: '患者姓名', pattern: /(?:姓\s*名|性名|娃名|佳名|始名|姨名)(?:\s*[:：]\s*|\s+)([\u4e00-\u9fa5a-zA-Z]{2,6})(?:\s|$)/, desc: '姓名+冒号/空格' },
        // 2. 紧贴格式（OCR可能把空格识别掉）- 只匹配2-4个字的中文姓名
        { field: '患者姓名', pattern: /(?:姓\s*名|性名)([\u4e00-\u9fa5]{2,4})(?:\s|$)/, desc: '姓名+紧贴姓名' },
        { field: '患者姓名', pattern: /^名(?:\s*[:：]\s*|\s+)([\u4e00-\u9fa5a-zA-Z]{2,6})(?:\s|$)/, desc: '名+冒号/空格' },
        { field: '患者姓名', pattern: /NAME(?:\s*[:：]\s*|\s+)([\u4e00-\u9fa5a-zA-Z]{2,6})(?:\s|$)/i, desc: 'NAME+冒号/空格' },
        
        // 申请医师 - 支持多种格式
        // 1. 带冒号或空格分隔
        { field: '申请医师', pattern: /(?:申请医师|申请医生|送检医生|送检医师)(?:\s*[:：]\s*|\s+)([\u4e00-\u9fa5a-zA-Z]{2,6})(?:\s|$)/, desc: '申请医师/送检医生+冒号/空格' },
        // 2. 紧贴格式（OCR可能把空格识别掉）
        { field: '申请医师', pattern: /(?:申请医师|申请医生|送检医生|送检医师)([\u4e00-\u9fa5]{2,4})(?:\s|$)/, desc: '申请医师/送检医生+紧贴' },
        { field: '申请医师', pattern: /DOCTOR\s*[:：]?\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: 'DOCTOR' },
        
        // 接收者 - 支持中英文混合格式
        { field: '接收者', pattern: /接收者\s+REC\.?\s*USER\s*[:：]?\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: '接收者 REC.USER' },
        { field: '接收者', pattern: /接收者(?:\s+REC\.?\s*USER)?\s*[:：]?\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: '接收者' },
        { field: '接收者', pattern: /REC\.?\s*USER\s*[:：]?\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: 'REC.USER' },
        
        // 检验者 - 支持中英文混合格式，包括紧贴格式  
        { field: '检验者', pattern: /检验者\s+(?:EXAMINER|EXMAMINER)\s*[:：]?\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: '检验者 EXAMINER' },
        { field: '检验者', pattern: /检验者(?:\s+(?:EXAMINER|EXMAMINER))?\s*[:：]?\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: '检验者' },
        { field: '检验者', pattern: /(?:检验者|检验员|操作者)\s*[:：]?\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: '检验者/员/操作者' },
        { field: '检验者', pattern: /(?:EXAMINER|EXMAMINER)\s*[:：]?([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: 'EXAMINER' },
        
        // 审核者 - 支持多种格式
        // 1. 带冒号或空格分隔
        { field: '审核者', pattern: /(?:审核者|审核员|复核者|复核员)(?:\s*[:：]\s*|\s+)([\u4e00-\u9fa5a-zA-Z]{2,6})(?:\s|$)/, desc: '审核者/复核者+冒号/空格' },
        // 2. 紧贴格式（OCR可能把空格识别掉）
        { field: '审核者', pattern: /(?:审核者|审核员|复核者|复核员)([\u4e00-\u9fa5]{2,4})(?:\s|$)/, desc: '审核者/复核者+紧贴' },
        // 3. 中英文混合格式
        { field: '审核者', pattern: /(?:审核者|复核者)\s+CHECKER\s*[:：]?\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: '审核者/复核者 CHECKER' },
        { field: '审核者', pattern: /CHECKER\s*[:：]?\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: 'CHECKER' },
        
        // 其他可能的医务人员字段
        { field: '医师', pattern: /(?:主治医师|住院医师|医师)\s*[:：]\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: '医师' },
        { field: '护士', pattern: /(?:责任护士|护士)\s*[:：]\s*([\u4e00-\u9fa5a-zA-Z]{2,6})/, desc: '护士' }
    ];
    
    for (const { field, pattern, desc } of sensitivePatterns) {
        const match = cleanText.match(pattern);
        if (match && match[1]) {
            const extractedName = match[1].trim();
            
            // 黑名单：排除常见的非姓名词汇
            const blacklist = [
                '患者', '病人', '医生', '医师', '护士', '信息', '报告', '检查', 
                '诊断', '治疗', '药物', '费用', '日期', '时间', '编号', '科室',
                '住院', '门诊', '急诊', '手术', '检验', '影像', '病历', '记录',
                '主任', '主治', '副主任', '备注', '说明', '年龄', '性别', '身份'
            ];
            
            // 如果提取的名字在黑名单中或包含黑名单词汇，跳过
            const containsBlacklistWord = blacklist.some(word => extractedName.includes(word));
            if (blacklist.includes(extractedName) || containsBlacklistWord) {
                continue;
            }
            
            // 验证提取的名字是否合理（主要是中文字符，可能包含少量英文）
            if (/^[\u4e00-\u9fa5]{2,6}$/.test(extractedName) || 
                /^[\u4e00-\u9fa5a-zA-Z]{2,6}$/.test(extractedName)) {
                return { field, name: extractedName };
            }
        }
    }
    
    return null;
};

// 检查是否是姓名字段标识
const isNameFieldIndicator = (text) => {
    if (!text) return false;
    
    const cleanText = text.trim();
    
    // 检查是否是姓名字段标识（不包含实际姓名）
    const nameFieldPatterns = [
        /^(姓名|性名|娃名|佳名|始名|姨名)[:：\s]*$/,
        /^NAME[:：\s]*$/i,
        /^姓\s*名[:：\s]*$/,
        /^(姓|名)[:：\s]*$/  // 单独的"姓"或"名"字
    ];
    
    for (const pattern of nameFieldPatterns) {
        if (pattern.test(cleanText)) {
            return true;
        }
    }
    
    // 检查是否包含姓名相关的关键词但不是完整的姓名
    if (cleanText.includes('姓名') || cleanText.toLowerCase().includes('name')) {
        // 排除完整的姓名行（包含实际姓名的情况）
        if (!/[\u4e00-\u9fa5]{2,4}/.test(cleanText.replace(/[姓名NAME:\s]/gi, ''))) {
            return true;
        }
    }
    
    return false;
};

// 检查是否是可能的患者姓名
const isPossiblePatientName = (text) => {
    if (!text) return false;
    
    const cleanText = text.trim();
    
    // 基本格式检查：2-4个中文字符
    if (!/^[\u4e00-\u9fa5]{2,4}$/.test(cleanText)) {
        return false;
    }
    
    // 排除明显的医学术语和检验项目
    const excludePatterns = [
        /^(血常规|尿常规|肝功能|肾功能|血糖|血脂|心电图)$/,
        /^(正常|异常|阳性|阴性|升高|降低|偏高|偏低)$/,
        /^(男性|女性|成人|儿童|老年|青年)$/,
        /^(医生|护士|技师|主任|教授|大夫|医师)$/,
        /^(科室|病房|门诊|住院|急诊|体检|检查)$/,
        /^(报告|结果|建议|诊断|治疗|用药)$/,
        /^(标本|样本|血液|尿液|粪便)$/
    ];
    
    for (const pattern of excludePatterns) {
        if (pattern.test(cleanText)) {
            return false;
        }
    }
    
    // 检查是否包含常见的患者姓氏
    const commonSurnames = ['王', '李', '张', '刘', '陈', '杨', '赵', '黄', '周', '吴', '徐', '孙', '胡', '朱', '高', '林', '何', '郭', '马', '罗', '梁', '宋', '郑', '谢', '韩', '唐'];
    const firstChar = cleanText.charAt(0);
    
    // 如果以常见姓氏开头，大概率是患者姓名
    if (commonSurnames.includes(firstChar)) {
        return true;
    }
    
    // 检查是否符合常见的中文姓名特征
    if (cleanText.length >= 2 && cleanText.length <= 4) {
        // 排除重复字符（如"三三"这种不太像真实姓名的）
        if (!/^(.)\1+$/.test(cleanText)) {
            return true;
        }
    }
    
    return false;
};

// 计算姓名得分
const calculateNameScore = (text, yDistance, isInSameRow, isToTheRight) => {
    let score = 50; // 基础分数
    
    // Y坐标距离评分
    score += Math.max(0, 30 - Math.floor(yDistance / 3));
    
    // 位置关系评分
    if (isInSameRow) score += 25;
    if (isToTheRight) score += 20;
    
    // 长度评分
    const lengthScore = { 2: 15, 3: 12, 4: 8 }[text.length] || 0;
    score += lengthScore;
    
    // 常见姓氏评分
    const commonSurnames = ['王', '李', '张', '刘', '陈', '杨', '赵', '黄', '周', '吴', '徐', '孙', '胡', '朱', '高', '林', '何', '郭', '马', '罗', '梁', '宋', '郑', '谢', '韩', '唐'];
    if (commonSurnames.includes(text.charAt(0))) score += 8;
    
    // 真实姓名特征
    if (!/^(.)\1+$/.test(text)) score += 5; // 不是重复字符
    if (/^[\u4e00-\u9fa5]{2,}$/.test(text)) score += 5; // 中文姓名
    
    return score;
};

// 查找实际患者姓名
const findActualPatientName = (allLines, nameFieldIndicatorLines) => {
    // 为每个姓名字段标识寻找附近的候选姓名
    let candidates = [];
    
    for (const indicator of nameFieldIndicatorLines) {
        const indicatorBox = indicator.line.box;
        const indicatorY = (indicatorBox[0][1] + indicatorBox[2][1]) / 2;
        const indicatorRight = Math.max(indicatorBox[0][0], indicatorBox[1][0], indicatorBox[2][0], indicatorBox[3][0]);
        
        // 在所有行中寻找位置合适的候选姓名
        for (let i = 0; i < allLines.length; i++) {
            const line = allLines[i];
            const text = line.text.trim();
            
            // 跳过空行和已知的字段标识行
            if (!text || isNameFieldIndicator(text) || isNotNameContent(text)) {
                continue;
            }
            
            const box = line.box;
            const lineY = (box[0][1] + box[2][1]) / 2;
            const lineLeft = Math.min(box[0][0], box[1][0], box[2][0], box[3][0]);
            
            // 计算与姓名字段标识的位置关系
            const yDistance = Math.abs(lineY - indicatorY);
            const isInSameRow = yDistance <= 30; // Y坐标差异小于30像素认为在同一行
            const isToTheRight = lineLeft > indicatorRight - 150; // 在右侧或稍有重叠，放宽范围
            
            // 验证是否是有效的姓名格式
            if (isPossiblePatientName(text) && yDistance <= 80) { // 扩大Y距离范围到80像素
                const score = calculateNameScore(text, yDistance, isInSameRow, isToTheRight);
                candidates.push({
                    line: line,
                    index: i,
                    name: text,
                    score: score,
                    yDistance: yDistance,
                    indicator: indicator.text,
                    isInSameRow: isInSameRow,
                    isToTheRight: isToTheRight
                });
            }
        }
    }
    
    // 选择得分最高的候选姓名
    if (candidates.length > 0) {
        candidates.sort((a, b) => b.score - a.score);
        const bestCandidate = candidates[0];
        
        // 找到与最佳候选姓名关联的姓名字段标识
        const associatedIndicator = nameFieldIndicatorLines.find(indicator => 
            indicator.text === bestCandidate.indicator
        );
        
        if (!associatedIndicator) {
            return null;
        }
        
        // 返回完整的信息结构
        return {
            // 实际患者姓名信息
            actualNameLine: bestCandidate.line,
            actualNameIndex: bestCandidate.index,
            name: bestCandidate.name,
            // 姓名字段标识信息
            fieldIndicatorLine: associatedIndicator.line,
            fieldIndicatorIndex: associatedIndicator.index,
            fieldIndicatorText: associatedIndicator.text,
            // 其他信息
            score: bestCandidate.score,
            yDistance: bestCandidate.yDistance
        };
    }
    return null;
};

// 检查字段名
const checkFieldName = (text) => {
    if (!text) return null;
    
    const cleanText = text.trim();
    
    // 检查是否是独立的字段名
    const fieldPatterns = [
        { field: '申请医师', pattern: /^(?:申请医师|申请医生|DOCTOR)\s*[:：]?\s*$/ },
        { field: '接收者', pattern: /^(?:接收者|REC\.?\s*USER)\s*[:：]?\s*$/ },
        { field: '检验者', pattern: /^(?:检验者|检验员|EXAMINER|EXMAMINER)\s*[:：]?\s*$/ },
        { field: '审核者', pattern: /^(?:审核者|审核员|CHECKER)\s*[:：]?\s*$/ }
    ];
    
    for (const { field, pattern } of fieldPatterns) {
        if (pattern.test(cleanText)) {
            return field;
        }
    }
    
    return null;
};

// 查找附近的姓名
const findNearbyName = (lines, currentIndex, fieldType) => {
    // 在当前行的前后几行中寻找可能的姓名
    const searchRange = 3; // 搜索前后3行
    const startIndex = Math.max(0, currentIndex - searchRange);
    const endIndex = Math.min(lines.length - 1, currentIndex + searchRange);
    
    for (let i = startIndex; i <= endIndex; i++) {
        if (i === currentIndex) continue; // 跳过当前行
        
        const line = lines[i];
        const text = line.text.trim();
        
        // 检查这行是否可能是姓名
        if (isPossibleName(text)) {
            return {
                line: line,
                field: fieldType,
                name: text,
                text: text
            };
        }
    }
    
    return null;
};

// 检查是否是可能的姓名
const isPossibleName = (text) => {
    if (!text) return false;
    
    const cleanText = text.trim();
    
    // 基本格式检查：2-6个字符，主要是中文
    if (!/^[\u4e00-\u9fa5a-zA-Z]{2,6}$/.test(cleanText)) {
        return false;
    }
    
    // 排除明显不是姓名的内容
    const excludePatterns = [
        /^\d+$/, // 纯数字
        /^[A-Z]+$/, // 纯大写字母
        /病房|科室|床号|住院号|检验|医院|医疗|临床/,
        /时间|日期|编号|号码/,
        /^(科|室|院|部|处|组|队)$/
    ];
    
    for (const pattern of excludePatterns) {
        if (pattern.test(cleanText)) {
            return false;
        }
    }
    
    return true;
};

// 检查是否不是姓名内容
const isNotNameContent = (text) => {
    if (!text) return true;
    
    const cleanText = text.trim();
    
    // 合并所有排除条件
    const excludePatterns = [
        /^\d+(\.\d+)?$/, // 纯数字
        /^\d{4}-\d{1,2}-\d{1,2}$/, /^\d{1,2}-\d{1,2}$/, // 日期格式
        /^\d{1,2}:\d{2}(:\d{2})?$/, // 时间格式
        /^(U\/L|ug\/dl|mmol\/L|g\/L|%)$/i, // 单位
        /^(ALT|AST|GGT|ALP|TP|ALB|GLB|CHE|UA|Cr|BUN)$/i, // 检验项目
        /^(Na|K|Cl|CO2|Ca|P|Mg|Fe)$/i, // 化学元素
        /^(CORTISOL|EXAMINER|CHECKER|REC|WARD|BED)$/i, // 字段名
        /^(NO|DATE|TIME|EPISODE|SPECIMEN)$/i, // 标识符
        /^\d+号$/, /^第\d+/, // 编号
        /病房|科室|床号|住院号/, // 医院术语
        /[A-Za-z]\d|\d[A-Za-z]/ // 字母数字混合
    ];
    
    return excludePatterns.some(pattern => pattern.test(cleanText)) || cleanText.length > 6;
};

// 计算文字行的坐标信息
const calculateLineCoordinates = (box) => {
    const x1 = Math.min(box[0][0], box[1][0], box[2][0], box[3][0]);
    const y1 = Math.min(box[0][1], box[1][1], box[2][1], box[3][1]);
    const x2 = Math.max(box[0][0], box[1][0], box[2][0], box[3][0]);
    const y2 = Math.max(box[0][1], box[1][1], box[2][1], box[3][1]);
    
    return { x1, y1, x2, y2, width: x2 - x1, height: y2 - y1 };
};

// 确保打码区域在画布范围内
const ensureMaskBounds = (x, y, width, height, canvasWidth, canvasHeight) => {
    const finalX = Math.max(0, Math.floor(x));
    const finalY = Math.max(0, Math.floor(y));
    const finalWidth = Math.min(canvasWidth - finalX, Math.ceil(width));
    const finalHeight = Math.min(canvasHeight - finalY, Math.ceil(height));
    
    return { x: finalX, y: finalY, width: finalWidth, height: finalHeight };
};

// 应用黑色遮挡
const applyBlackBlock = (ctx, x, y, width, height) => {
    // 确保打码区域在画布范围内
    x = Math.max(0, Math.floor(x));
    y = Math.max(0, Math.floor(y));
    width = Math.min(ctx.canvas.width - x, Math.ceil(width));
    height = Math.min(ctx.canvas.height - y, Math.ceil(height));
    
    if (width <= 0 || height <= 0) return;
    
    try {
        // 使用纯黑色色块覆盖
        ctx.fillStyle = '#000000';
        ctx.fillRect(x, y, width, height);
    } catch (error) {
        console.error('❌ 应用黑色色块时出错:', error);
    }
};

// 应用整行打码
const applyFullLineMask = (ctx, box, canvasWidth, canvasHeight, field) => {
    const coords = calculateLineCoordinates(box);
    const padding = 3;
    
    const maskBounds = ensureMaskBounds(
        coords.x1 - padding,
        coords.y1 - padding,
        coords.width + padding * 2,
        coords.height + padding * 2,
        canvasWidth,
        canvasHeight
    );
    
    if (maskBounds.width > 0 && maskBounds.height > 0) {
        applyBlackBlock(ctx, maskBounds.x, maskBounds.y, maskBounds.width, maskBounds.height);
        return true;
    }
    return false;
};

// 应用精确打码
const applyPreciseMask = (ctx, box, lineText, name, canvasWidth, canvasHeight, field) => {
    const nameIndex = lineText.indexOf(name);
    if (nameIndex === -1) return false;
    
    const coords = calculateLineCoordinates(box);
    const prefixLength = nameIndex;
    const nameLength = name.length;
    const totalLength = lineText.length;
    
    const nameStartRatio = prefixLength / totalLength;
    const nameWidthRatio = nameLength / totalLength;
    
    const nameStartX = coords.x1 + coords.width * nameStartRatio;
    const nameWidth = coords.width * nameWidthRatio;
    
    const padding = 3;
    const maskBounds = ensureMaskBounds(
        nameStartX - padding,
        coords.y1 - padding,
        nameWidth + padding * 2,
        coords.height + padding * 2,
        canvasWidth,
        canvasHeight
    );
    
    if (maskBounds.width > 0 && maskBounds.height > 0) {
        applyBlackBlock(ctx, maskBounds.x, maskBounds.y, maskBounds.width, maskBounds.height);
        return true;
    }
    return false;
};

// 设置PDF.js worker
const setupPdfWorker = () => {
    // 检查PDF.js是否已加载，支持多种可能的全局变量名
    let pdfLib = null;
    if (typeof window !== 'undefined') {
        pdfLib = window.pdfjsLib || window.PDFJS || window['pdfjs-dist'] || null;
    }
    
    if (!pdfLib) {
        // 如果全局变量还没有，等待一下再尝试
        console.warn('PDF.js尚未完全加载，请稍后重试...');
        return null;
    }
    
    // 设置worker路径（如果还没设置的话）
    if (!pdfLib.GlobalWorkerOptions.workerSrc) {
        pdfLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
    }
    
    return pdfLib;
};

// PDF转换为图片
export const convertPdfToImages = async (file, progressCallback) => {
    if (!progressCallback) progressCallback = () => {};
    
    // 等待PDF.js加载完成
    let pdfLib = setupPdfWorker();
    if (!pdfLib) {
        // 等待最多5秒让PDF.js加载完成
        for (let i = 0; i < 50; i++) {
            await new Promise(resolve => setTimeout(resolve, 100));
            pdfLib = setupPdfWorker();
            if (pdfLib) break;
        }
    }
    
    if (!pdfLib) {
        throw new Error('PDF.js未能正确加载，请刷新页面重试');
    }

    try {
        progressCallback(10, '正在分析PDF文件...');
        
        const arrayBuffer = await file.arrayBuffer();
        
        // 配置 PDF 加载选项，使用本地的 CMap 和字体文件
        const loadingTask = pdfLib.getDocument({
            data: arrayBuffer,
            // 使用本地 CMap 文件（处理中文等字符）
            cMapUrl: '/pdfjs/cmaps/',
            cMapPacked: true,
            // 使用本地标准字体
            standardFontDataUrl: '/pdfjs/standard_fonts/',
            // 容错配置
            verbosity: 0,
            ignoreErrors: true,
        });
        
        const pdf = await loadingTask.promise;
        const totalPages = pdf.numPages;
        
        progressCallback(15, `发现${totalPages}页，开始转换...`);
        
        const images = [];
        
        // 处理每一页
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
            progressCallback(15 + (pageNum - 1) * 60 / totalPages, `正在转换第${pageNum}/${totalPages}页...`);
            
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: 3.0 }); // 高质量转换
            
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            
            // 设置高质量绘制参数
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            
            const renderContext = {
                canvasContext: context,
                viewport: viewport
            };
            
            await page.render(renderContext).promise;
            
            // 转换为Image对象，保持原始像素尺寸
            const img = await new Promise((resolve) => {
                const image = new Image();
                image.onload = () => resolve(image);
                image.src = canvas.toDataURL('image/png'); // 使用PNG格式保证质量
            });
            
            images.push({
                image: img,
                pageNumber: pageNum,
                title: `第${pageNum}页`,
                fileName: `${file.name}_page_${pageNum}.png`,
                originalWidth: img.width,
                originalHeight: img.height
            });
        }
        
        progressCallback(75, `所有页面转换完成，共${totalPages}页`);
        return images;
        
    } catch (error) {
        console.error('PDF转换失败:', error);
        throw new Error(`PDF转换失败: ${error.message}`);
    }
};

// 从Image对象创建File对象（保持像素一致）
export const imageToFile = (image, fileName, format = null) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // 保持原始像素尺寸
        canvas.width = image.width;
        canvas.height = image.height;
        
        // 设置最高质量绘制参数
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // 绘制图片（保持原始尺寸）
        ctx.drawImage(image, 0, 0);
        
        // 对于打码处理后的图片，统一使用PNG格式以确保质量和兼容性
        let finalFormat = 'image/png';
        let quality = 1.0;
        let finalFileName = fileName;
        
        if (format) {
            // 如果明确指定了格式，使用指定的格式
            finalFormat = format;
            if (format === 'image/jpeg') {
                quality = 0.95;
            }
        } else {
            // 自动处理：对于打码后的图片，统一使用PNG格式
            // 确保文件名有正确的PNG扩展名
            const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
            finalFileName = nameWithoutExt + '.png';
            finalFormat = 'image/png';
            quality = 1.0;
        }
        
        // 转换为File对象
        canvas.toBlob((blob) => {
            const file = new File([blob], finalFileName, {
                type: finalFormat,
                lastModified: Date.now()
            });
            resolve(file);
        }, finalFormat, quality);
    });
};

// 检查文件是否为PDF
export const isPdfFile = (file) => {
    return file && file.type === 'application/pdf';
};

// 检查文件是否为图片
export const isImageFile = (file) => {
    return file && file.type.startsWith('image/');
};

// 主要的敏感信息打码函数
export const maskSensitiveInfo = async (image, ocrResult) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // 保持原始图片的像素尺寸，不进行任何缩放
    const { width, height } = image;
    canvas.width = width;
    canvas.height = height;
    
    // 设置最高质量绘制参数
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // 绘制原始图片（保持原始像素尺寸）
    ctx.drawImage(image, 0, 0, width, height);
    
    let maskedCount = 0;
    const sensitiveInfoFound = [];
    let patientNameLineIndex = -1;
    
    // 第一步：寻找所有敏感信息和患者姓名字段标识
    for (let i = 0; i < ocrResult.src.length; i++) {
        const line = ocrResult.src[i];
        const text = line.text.trim();
        
        // 检查是否包含敏感信息的完整行
        const sensitiveMatch = extractSensitiveInfoFromLine(text);
        if (sensitiveMatch) {
            sensitiveInfoFound.push({
                line: line,
                field: sensitiveMatch.field,
                name: sensitiveMatch.name,
                text: text,
                lineIndex: i
            });
            
            // 记录患者姓名字段所在的行索引
            if (sensitiveMatch.field === '患者姓名') {
                patientNameLineIndex = i;
            }
        } else {
            // 检查是否是独立的字段名（如"REC.USER:"、"EXAMINER:"等）
            const fieldMatch = checkFieldName(text);
            if (fieldMatch) {
                // 寻找下一行或附近行的姓名
                const nameFound = findNearbyName(ocrResult.src, i, fieldMatch);
                if (nameFound) {
                    nameFound.lineIndex = i;
                    sensitiveInfoFound.push(nameFound);
                }
            }
        }
    }
    
    // 第二步：如果没有找到完整的患者姓名行，尝试寻找姓名字段标识和实际姓名
    if (patientNameLineIndex === -1) {
        // 寻找姓名字段标识行
        let nameFieldIndicatorLines = [];
        for (let i = 0; i < ocrResult.src.length; i++) {
            const line = ocrResult.src[i];
            const text = line.text.trim();
            
            // 检查是否是姓名字段标识
            if (isNameFieldIndicator(text)) {
                nameFieldIndicatorLines.push({ line, index: i, text });
            }
        }
        
        // 如果找到姓名字段标识，寻找附近的实际姓名
        if (nameFieldIndicatorLines.length > 0) {
            const patientNameInfo = findActualPatientName(ocrResult.src, nameFieldIndicatorLines);
            if (patientNameInfo) {
                // 使用姓名字段标识所在行作为基准行
                patientNameLineIndex = patientNameInfo.fieldIndicatorIndex;
                
                // 添加到敏感信息列表
                sensitiveInfoFound.push({
                    line: patientNameInfo.actualNameLine, // 实际姓名行用于打码
                    field: '患者姓名',
                    name: patientNameInfo.name,
                    text: patientNameInfo.name,
                    lineIndex: patientNameInfo.actualNameIndex,
                    fieldIndicatorLine: patientNameInfo.fieldIndicatorLine, // 保存字段标识行信息
                    fieldIndicatorIndex: patientNameInfo.fieldIndicatorIndex
                });
            }
        }
    }
    
    // 如果找到了患者姓名字段，则对其之前的整个区域进行完整打码
    if (patientNameLineIndex !== -1) {
        // 找到姓名字段标识所在行的Y坐标（这是关键修正）
        const nameFieldLine = ocrResult.src[patientNameLineIndex]; // 这里是姓名字段标识行
        const nameFieldBox = nameFieldLine.box;
        const nameFieldY = Math.min(nameFieldBox[0][1], nameFieldBox[1][1], nameFieldBox[2][1], nameFieldBox[3][1]);
        
        // 添加一个特殊的区域打码标记
        sensitiveInfoFound.push({
            field: '医院标识和患者信息保护',
            name: '顶部区域完整遮盖',
            text: '顶部区域完整遮盖',
            lineIndex: -1,
            isTopAreaMask: true, // 标记为顶部区域完整打码
            maskHeight: nameFieldY + 10, // 稍微扩展一点，确保完全覆盖
            basedOnFieldIndicator: true // 标记基于字段标识行进行打码
        });
    }
    
    // 对找到的所有敏感信息进行打码
    for (const info of sensitiveInfoFound) {
        const { line, field, name, text, isFullLineMask, isTopAreaMask, maskHeight } = info;
        
        if (isTopAreaMask) {
            // 顶部区域完整遮盖模式
            const maskX = 0; // 从图片左边缘开始
            const maskY = 0; // 从图片顶部开始
            const maskWidth = width; // 整个图片宽度
            const finalMaskHeight = Math.min(height, maskHeight) - 13; // 到患者姓名字段的高度
            
            // 应用顶部区域黑色遮盖
            applyBlackBlock(ctx, maskX, maskY, maskWidth, finalMaskHeight);
            maskedCount++;
            
            continue; // 跳过后续处理，因为是区域遮盖而不是基于文字行的遮盖
        }
        
        // 对文字行进行打码
        const success = isFullLineMask 
            ? applyFullLineMask(ctx, line.box, width, height, field)
            : applyPreciseMask(ctx, line.box, text, name, width, height, field);
        
        if (success) {
            maskedCount++;
        }
    }
    
    if (maskedCount === 0) {
        console.log('未找到敏感信息，无法进行打码');
    }
    
    // 转换为Image对象返回，使用最高质量输出
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.src = canvas.toDataURL('image/png');
    });
};
